import { z } from 'zod';

const optStr = (max = 200) => z.preprocess(
    (v) => (v === null || v === undefined ? undefined : v),
    z.string().max(max).optional().or(z.literal(''))
);

/* ── Chi tiết báo giá (dòng sản phẩm) ── */
export const quotationDetailSchema = z.object({
    id: optStr(100),
    productId: optStr(100),
    productCode: optStr(50),
    productName: z.string().min(1, 'Tên SP không được trống'),
    specs: optStr(200),
    process: optStr(1000),
    unit: optStr(20),
    quantity: z.coerce.number().int().min(1, 'SL >= 1'),
    priceVND: z.coerce.number().min(0, 'Đơn giá >= 0'),
    priceUSD: z.coerce.number().min(0).optional(),
    costPrice: z.coerce.number().min(0).optional(),
    profitMargin: z.coerce.number().min(0).max(100).optional(),
    notes: optStr(500),
    isCombo: z.boolean().optional(),
    isCommercial: z.boolean().optional(),
    productionPrice: z.coerce.number().min(0).optional(),
    quotedPrice: z.coerce.number().min(0, 'Giá báo chốt phải >= 0'),
    isComposite: z.boolean().optional(),
});

export type QuotationDetailFormData = z.infer<typeof quotationDetailSchema>;

/* ── Form chính ── */
export const quotationFormSchema = z.object({
    // — Thông tin báo giá —
    quotationId: optStr(30),
    customerId: z.string().min(1, 'Vui lòng chọn khách hàng'),
    customerName: optStr(),
    creatorId: optStr(),

    // — Ngày tháng —
    createdDate: z.string().min(1, 'Ngày tạo không được để trống'),
    validUntil: z.string().min(1, 'Ngày hiệu lực không được để trống'),

    // — Chi tiết sản phẩm (N dòng) —
    details: z.array(quotationDetailSchema)
        .min(1, 'Phải có ít nhất 1 sản phẩm')
        .refine(
            (items) => items.some(item => item.productId && item.productId.trim() !== ''),
            { message: 'Báo giá phải có ít nhất 1 sản phẩm đã chọn' }
        ),

    // — Tài chính (tổng hợp) —
    vatRate: z.coerce.number().min(0).max(100).optional(),
    currency: z.enum(['VND', 'USD']).optional(),
    exchangeRate: z.coerce.number().min(0).optional(),
    shippingFee: z.coerce.number().min(0).optional(),
    deposit: optStr(200),
    paymentTerm: optStr(200),
    margin: z.coerce.number().min(0).max(100).optional(),

    // — Giao hàng & Liên hệ —
    deliveryAddress: optStr(500),
    contactPerson: optStr(100),
    phone: optStr(20),
    email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
    deliveryTime: optStr(100),

    // — Trạng thái —
    status: z.enum(['DRAFT', 'SENT', 'PROCESSING', 'ESTIMATED', 'APPROVED', 'REJECTED']), // khớp QuotationStatus trong DB
    progress: optStr(200),
    notes: optStr(2000),
    quantityTolerance: optStr(100),
    isNonMoq: z.boolean().default(false).optional(),
    isOutsourced: z.boolean().default(false).optional(),
    supplierCost: z.coerce.number().min(0).optional(),
});

export type QuotationFormData = z.infer<typeof quotationFormSchema>;

export interface QuotationDetail {
    id?: string;
    productId?: string | null;
    productCode?: string | null;
    productName: string;
    productGroup?: string | null;
    image?: string | null;
    process?: string | null;
    materialId?: string | null;
    materialName?: string | null;
    specs?: string | null;
    unit?: string | null;
    quantity: number;
    priceVND: number;
    priceUSD?: number | null;
    costPrice?: number | null;
    profitMargin?: number | null;
    status?: string | null;
    notes?: string | null;
    tags?: string | null;
    isCombo?: boolean;
    isCommercial?: boolean;
    productionPrice?: number | null;
    quotedPrice?: number | null;
    isComposite?: boolean;
}

export interface Quotation {
    id: string;
    quotationId: string;
    version?: number;
    customerId?: string | null;
    customerName?: string | null;
    createdDate: string;
    validUntil: string;
    approvedDate?: string | null;
    vatRate?: number | null;
    currency?: string | null;
    exchangeRate?: number | null;
    shippingFee?: number | null;
    deposit?: string | null;
    paymentTerm?: string | null;
    margin?: number | null;
    deliveryAddress?: string | null;
    contactPerson?: string | null;
    phone?: string | null;
    email?: string | null;
    deliveryTime?: string | null;
    progress?: string | null;
    status: 'DRAFT' | 'SENT' | 'PROCESSING' | 'ESTIMATED' | 'APPROVED' | 'REJECTED';
    creatorId?: string | null;
    creator?: { id: string; fullName: string } | null;
    approverId?: string | null;
    approver?: { id: string; fullName: string } | null;
    notes?: string | null;
    quantityTolerance?: string | null;
    customer?: { id: string; code?: string; name: string; shortName?: string; address?: string; phone?: string } | null;
    details?: QuotationDetail[];
    isNonMoq?: boolean;
    isOutsourced?: boolean;
    supplierCost?: number | null;
    createdAt: string;
    updatedAt: string;
}
